import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaUnlock,
  FaUndo,
  FaCheck,
  FaTimes,
  FaEdit,
  FaPause,
} from "react-icons/fa";
import {
  LuBarChart2,
  LuInfo,
} from "react-icons/lu";
import { FiClock } from "react-icons/fi";
import lodash from "lodash";
import cronstrue from "cronstrue";
import { MdAnalytics, MdOutlineIntegrationInstructions, MdSchedule } from "react-icons/md";
import { PiPresentationFill } from "react-icons/pi";
import { ScheduledJobProto, ScheduledScenarioJobProto, ScheduledJobType } from "build-ts/schedule";
import { SlackChannel, SlackNotificationConfig, SlackNotificationRequest } from "build-ts/slack_notification";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import { getSlackChannels } from "services/AnalystService";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { BiTargetLock } from "react-icons/bi";
import { useUser } from "context/UserContext";

type TabId = "analysis" | "instructions" | "schedule";

interface ScenarioArtifactCardProps {
  /** SCHEDULED_JOB artifact (from reasoning message) */
  scheduleJob?: ScheduledJobProto | null;
  /** Analyst id for saving the schedule */
  analystId?: string;
  /** Whether this job is already saved */
  isSavedInAnalyst: boolean;
  /** Whether this is the most-recently selected card */
  isSelected?: boolean;
  /** Called when user removes the artifact card */
  onRemove: () => void;
  /** Called after the job is successfully saved, with the persisted job */
  onSave?: (savedJob: ScheduledJobProto) => void;
  /** Start expanded */
  defaultExpanded?: boolean;
  /** When this value changes, collapse the card */
  collapseSignal?: number;
  /** When this value changes, expand the card */
  expandSignal?: number;
}

const ScenarioArtifactCard: React.FC<ScenarioArtifactCardProps> = ({
  scheduleJob: initialScheduleJob,
  analystId,
  isSavedInAnalyst,
  isSelected = false,
  onRemove,
  onSave,
  defaultExpanded = true,
  collapseSignal,
  expandSignal,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { userId } = useUser();

  useEffect(() => {
    if (collapseSignal) {
      setIsExpanded(false);
    }
  }, [collapseSignal]);

  useEffect(() => {
    if (expandSignal) {
      setIsExpanded(true);
    }
  }, [expandSignal]);

  const [activeTab, setActiveTab] = useState<TabId>("analysis");
  const [notesLocked, setNotesLocked] = useState(true);

  // Form state — initialised from scheduleJob
  const [jobName, setJobName] = useState(initialScheduleJob?.header?.name ?? "");
  const [trigger, setTrigger] = useState(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
  const [reportingInstructions, setReportingInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? ""
  );
  const [analysisInstructions, setAnalysisInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? ""
  );
  const [historicalInstructions, setHistoricalInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.historical_instructions ?? ""
  );
  const [temporaryInstructions, setTemporaryInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? ""
  );

  // Schedule tab state
  const [cronExpression, setCronExpression] = useState(initialScheduleJob?.cron_expr ?? "");
  const [isScheduleActive, setIsScheduleActive] = useState(initialScheduleJob?.is_active ?? false);
  const [slackTitle, setSlackTitle] = useState(
    initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? ""
  );
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(() => {
    const ch = initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
    return ch?.id || ch?.name ? (ch as SlackChannel) : null;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showCronHumanReadable, setShowCronHumanReadable] = useState(true);

  const initialSlackChannel = (() => {
    const ch = initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
    return ch?.id || ch?.name ? (ch as SlackChannel) : null;
  })();

  const isChanged =
    !lodash.isEqual(jobName, initialScheduleJob?.header?.name ?? "") ||
    !lodash.isEqual(trigger, initialScheduleJob?.scheduled_analysis?.trigger ?? "") ||
    !lodash.isEqual(reportingInstructions, initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? "") ||
    !lodash.isEqual(analysisInstructions, initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? "") ||
    !lodash.isEqual(historicalInstructions, initialScheduleJob?.scheduled_analysis?.historical_instructions ?? "") ||
    !lodash.isEqual(temporaryInstructions, initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? "") ||
    !lodash.isEqual(cronExpression, initialScheduleJob?.cron_expr ?? "") ||
    !lodash.isEqual(isScheduleActive, initialScheduleJob?.is_active ?? false) ||
    !lodash.isEqual(slackTitle, initialScheduleJob?.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? "") ||
    !lodash.isEqual(selectedSlackChannel, initialSlackChannel);

  // Re-sync all fields when scheduleJob prop changes
  useEffect(() => {
    if (initialScheduleJob) {
      setJobName(initialScheduleJob.header?.name ?? "");
      setTrigger(initialScheduleJob.scheduled_analysis?.trigger ?? "");
      setReportingInstructions(initialScheduleJob.scheduled_analysis?.reporting_instructions ?? "");
      setAnalysisInstructions(initialScheduleJob.scheduled_analysis?.analysis_instructions ?? "");
      setHistoricalInstructions(initialScheduleJob.scheduled_analysis?.historical_instructions ?? "");
      setTemporaryInstructions(initialScheduleJob.scheduled_analysis?.temporary_instructions ?? "");
      setCronExpression(initialScheduleJob.cron_expr ?? "");
      setIsScheduleActive(initialScheduleJob.is_active ?? false);
      setSlackTitle(
        initialScheduleJob.scheduled_analysis?.slack_notification_request?.notification_config?.message_title ?? ""
      );
      const channel = initialScheduleJob.scheduled_analysis?.slack_notification_request?.notification_config?.channel;
      setSelectedSlackChannel(channel?.id || channel?.name ? (channel as SlackChannel) : null);
    } else {
      setJobName("");
      setTrigger("");
      setReportingInstructions("");
      setAnalysisInstructions("");
      setHistoricalInstructions("");
      setTemporaryInstructions("");
      setCronExpression("");
      setIsScheduleActive(false);
      setSlackTitle("");
      setSelectedSlackChannel(null);
    }
  }, [initialScheduleJob]);

  // Fetch Slack channels
  useEffect(() => {
    const fetchChannels = async () => {
      const channels = await getSlackChannels();
      if (channels) setSlackChannels(channels);
    };
    fetchChannels();
  }, []);

  const handleSave = async () => {
    if (!analystId) {
      showToast("Analyst context is required to save.", "", "warning");
      return;
    }
    if (jobName.trim() === "") {
      showToast("Please enter a job name", "", "warning");
      return;
    }
    setIsSaving(true);
    try {
      const job = ScheduledJobProto.create({
        header: Header.create({
          id: initialScheduleJob?.header?.id ?? uuidv4(),
          name: jobName.trim(),
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: analystId,
        }),
        type: ScheduledJobType.SCENARIO_JOB,
        cron_expr: cronExpression,
        is_active: isScheduleActive,
        scheduled_analysis: ScheduledScenarioJobProto.create({
          trigger: trigger.trim(),
          analysis_instructions: analysisInstructions.trim(),
          reporting_instructions: reportingInstructions.trim(),
          historical_instructions: historicalInstructions.trim(),
          temporary_instructions: temporaryInstructions.trim(),
          slack_notification_request: SlackNotificationRequest.create({
            notification_config: SlackNotificationConfig.create({
              channel: selectedSlackChannel
                ? SlackChannel.create({
                  id: selectedSlackChannel.id,
                  name: selectedSlackChannel.name,
                  is_private: selectedSlackChannel.is_private,
                  num_members: selectedSlackChannel.num_members,
                })
                : undefined,
              message_title: slackTitle.trim(),
            }),
          }),
        }),
      });
      const response = await putScheduledJob(job);
      if (response) {
        showToast("Schedule saved successfully", "", "success");
        onSave?.(response);
      } else {
        showToast("Failed to save schedule", "", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setJobName(initialScheduleJob?.header?.name ?? "");
    setTrigger(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
    setReportingInstructions(initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? "");
    setAnalysisInstructions(initialScheduleJob?.scheduled_analysis?.analysis_instructions ?? "");
    setHistoricalInstructions(initialScheduleJob?.scheduled_analysis?.historical_instructions ?? "");
    setTemporaryInstructions(initialScheduleJob?.scheduled_analysis?.temporary_instructions ?? "");
  };

  const cronHumanReadable = (() => {
    if (!cronExpression.trim()) return null;
    try {
      return cronstrue.toString(cronExpression.trim());
    } catch {
      return null;
    }
  })();

  const displayLabel = jobName?.trim() || initialScheduleJob?.header?.name?.trim() || "New Job";

  const handleFieldChange = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<T>>
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setter(e.target.value as T);
  };

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "analysis", label: "Analysis", icon: LuBarChart2 },
    { id: "schedule", label: "Schedule", icon: MdSchedule },
    ...(userId === "02c73738-9481-4d0f-85c4-e927c6a09d09"
      ? [{ id: "instructions" as TabId, label: "Guidance", icon: MdOutlineIntegrationInstructions }]
      : []),
  ];

  return (
    <Box width="100%" bg="white" overflow="hidden">
      {/* ── Card Header ─────────────────────────────────── */}
      <HStack
        px={2}
        py={1}
        bg="gray.50"
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        justifyContent="space-between"
        borderBottom="1px solid"
        borderTop="1px solid"
        borderColor="gray.900"
        transition="background 0.15s"
      >
        <HStack spacing={2} flex={1} minW={0}>
          <Icon as={MdAnalytics} boxSize={6} color="purple.600" flexShrink={0} />
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.800"
            isTruncated
            mb={0}
            maxW="calc(100% - 60px)"
          >
            {displayLabel}
          </Text>

          {/* Saved / Draft badge */}
          {isSavedInAnalyst ? (
            <Text
              fontSize="2xs"
              color="green.800"
              bg="green.100"
              px={2}
              py={0.5}
              borderRadius="sm"
              fontWeight="700"
              flexShrink={0}
              mb={0}
              border="1px solid"
              borderColor="green.200"
            >
              SAVED
            </Text>
          ) : (
            <Text
              fontSize="2xs"
              color="orange.700"
              bg="yellow.100"
              px={2}
              py={0.5}
              borderRadius="sm"
              fontWeight="700"
              flexShrink={0}
              mb={0}
              border="1px solid"
              borderColor="yellow.500"
            >
              DRAFT
            </Text>
          )}
        </HStack>
        <IconButton
          size="xs"
          variant="ghost"
          icon={<Icon as={FaTimes} boxSize={4} />}
          color="red.400"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Close"
        />
        <Icon
          as={isExpanded ? FaChevronUp : FaChevronDown}
          boxSize={3}
          color="gray.600"
          mr={1}
        />
      </HStack>

      {/* ── Expanded Body ───────────────────────────── */}
      <Collapse in={isExpanded} animateOpacity>
        <Box>
          {/* ── Tab Bar ── */}
          <HStack
            spacing={0}
            borderBottom="1px solid"
            borderColor="gray.200"
            px={2}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <HStack
                  key={tab.id}
                  spacing={1.5}
                  px={3}
                  py={1.5}
                  cursor="pointer"
                  borderBottom="2px solid"
                  borderColor={isActive ? "purple.500" : "transparent"}
                  color={isActive ? "purple.600" : "gray.500"}
                  _hover={{ color: isActive ? "purple.600" : "gray.700" }}
                  transition="all 0.15s"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab(tab.id);
                  }}
                  mb="-1px"
                >
                  <Icon as={tab.icon} boxSize={4} />
                  <Text fontSize="xs" fontWeight={isActive ? "700" : "500"} mb={0}>
                    {tab.label}
                  </Text>
                </HStack>
              );
            })}
          </HStack>

          {/* ── Tab Content ── */}
          <Box
            px={0}
            pt={0}
            pb={0}
            overflowY="auto"
            maxH="520px"
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
            }}
          >
            {/* ── ANALYSIS TAB ── */}
            {activeTab === "analysis" && (
              <VStack spacing={2.5} align="stretch" p={2}>
                {/* Name */}
                <HStack spacing={2} align="center">
                  <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0} whiteSpace="nowrap" flexShrink={0}>
                    Name
                  </Text>
                  <Input
                    size="sm"
                    value={jobName}
                    onChange={handleFieldChange(setJobName)}
                    placeholder="e.g. Weekly CPL Monitor…"
                    _focus={{
                      borderColor: "purple.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                    }}
                    fontWeight="600"
                    fontSize="xs"
                    flex={1}
                  />
                </HStack>

                {/* Trigger + Reporting side by side */}
                <SimpleGrid columns={2} spacing={2} px={1}>
                  <FormControl>
                    <HStack spacing={1} mb={1} align="center">
                      <Icon as={BiTargetLock} boxSize={4} color="purple.700" />
                      <Text fontSize="xs" fontWeight="700" color="gray.700" mb={0}>
                        Analysis Objective
                      </Text>
                      <Tooltip
                        label="When should this alert fire? Define the metric thresholds and data conditions."
                        placement="top"
                        hasArrow
                        fontSize="xs"
                        bg="blue.50"
                        color="gray.700"
                        py={2}
                        px={2}
                        borderRadius="md"
                        fontWeight="600"
                      >
                        <span>
                          <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                    <Textarea
                      size="sm"
                      value={trigger}
                      onChange={handleFieldChange(setTrigger)}
                      placeholder="e.g. CPL_today > 100 AND CPL_3day_avg < 50, spend > 0…"
                      rows={9}
                      resize="none"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                      }}
                      fontWeight="500"
                      fontSize="xs"
                    />
                  </FormControl>

                  <FormControl>
                    <HStack spacing={1} mb={1} align="center">
                      <Icon as={PiPresentationFill} boxSize={4} color="purple.700" />
                      <Text fontSize="xs" fontWeight="700" color="gray.700" mb={0}>
                        Reporting Instructions
                      </Text>
                      <Tooltip
                        label="How should the alert be presented? Specify columns, ranking, and narrative format."
                        placement="top"
                        hasArrow
                        fontSize="xs"
                        maxW="220px"
                        bg="blue.50"
                        color="gray.700"
                        py={2}
                        px={2}
                        borderRadius="md"
                        fontWeight="600"
                      >
                        <span>
                          <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                    <Textarea
                      size="sm"
                      value={reportingInstructions}
                      onChange={handleFieldChange(setReportingInstructions)}
                      placeholder="e.g. Table of top 10 products by sales, include sales delta and price change %…"
                      rows={9}
                      resize="none"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                      }}
                      fontWeight="500"
                      fontSize="xs"
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}

            {/* ── INSTRUCTIONS TAB ── */}
            {userId === "02c73738-9481-4d0f-85c4-e927c6a09d09" && activeTab === "instructions" && (
              <VStack spacing={3} align="stretch">
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <HStack
                    px={2}
                    py={0}
                    bg="white"
                    justifyContent="space-between"
                    borderBottom="1px solid"
                    borderColor="gray.700"
                  >
                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color="gray.700"
                        fontFamily="mono"
                        ml={1}
                        mb={0}
                        fontWeight="600"
                      >
                        Internal Analysis Instructions
                      </Text>
                      <Text fontSize="xs" color="gray.700" mb={0} fontFamily="mono">
                        {notesLocked ? "(locked — click 🔒 to edit)" : "(editing...)"}
                      </Text>
                    </HStack>
                    <Tooltip
                      label={notesLocked ? "Unlock to edit" : "Lock editing"}
                      placement="top"
                      hasArrow
                      fontSize="xs"
                    >
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={
                          <Icon
                            as={notesLocked ? FaLock : FaUnlock}
                            boxSize={3}
                          />
                        }
                        aria-label={notesLocked ? "Unlock notes" : "Lock notes"}
                        color={notesLocked ? "gray.500" : "purple.600"}
                        _hover={{
                          color: notesLocked ? "gray.700" : "purple.700",
                          bg: "purple.50",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotesLocked((prev) => !prev);
                        }}
                      >
                        {notesLocked ? "Edit" : "Editing"}
                      </Button>
                    </Tooltip>
                  </HStack>

                  <Textarea
                    value={analysisInstructions}
                    onChange={handleFieldChange(setAnalysisInstructions)}
                    placeholder="# analysis instructions&#10;# how to build the data query…"
                    rows={10}
                    resize="none"
                    isReadOnly={notesLocked}
                    fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Source Code Pro', monospace"
                    fontSize="xs"
                    lineHeight="1.6"
                    bg={notesLocked ? "gray.50" : "white"}
                    color={notesLocked ? "gray.700" : "gray.800"}
                    borderRadius="none"
                    border="none"
                    _focus={{
                      border: "none",
                      boxShadow: "none",
                    }}
                    _readOnly={{
                      cursor: "default",
                      opacity: 1,
                      bg: "gray.50",
                      color: "gray.700",
                    }}
                    p={3}
                    sx={{
                      caretColor: "green.300",
                      "::placeholder": {
                        color: "gray.600",
                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      },
                    }}
                  />
                </Box>
              </VStack>
            )}

            {/* ── SCHEDULE TAB ── */}
            {activeTab === "schedule" && (
              <Box px={2} py={3}>
                <VStack spacing={2.5} align="stretch">
                  <FormControl>
                    <Flex gap={2} align="flex-end" w="100%">
                      <Box flex="3">
                        <FormLabel fontSize="xs" fontWeight="600" mb={0.5}>
                          <HStack spacing={1} align="center">
                            <Icon as={FiClock} boxSize={3.5} />
                            <span>{showCronHumanReadable ? "Run frequency" : "Cron expression"}</span>
                          </HStack>
                        </FormLabel>
                        <Tooltip
                          label={
                            showCronHumanReadable
                              ? cronExpression || "No cron expression set"
                              : cronHumanReadable || "Invalid or unparsable cron"
                          }
                          placement="top"
                          hasArrow
                          isDisabled={!cronExpression.trim() || !cronHumanReadable}
                        >
                          <InputGroup size="sm">
                            <Input
                              value={
                                showCronHumanReadable && cronHumanReadable
                                  ? cronHumanReadable
                                  : cronExpression
                              }
                              onChange={(e) => {
                                if (!showCronHumanReadable || !cronHumanReadable) {
                                  setCronExpression(e.target.value);
                                }
                              }}
                              placeholder="e.g. 0 0 5 ? * MON"
                              fontFamily={
                                showCronHumanReadable && cronHumanReadable ? "body" : "mono"
                              }
                              fontSize="xs"
                              h="2rem"
                              pr={
                                cronExpression.trim() && cronHumanReadable ? "1.75rem" : undefined
                              }
                              isReadOnly={showCronHumanReadable && !!cronHumanReadable}
                              _focus={{
                                borderColor: "purple.500",
                                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                              }}
                            />
                            {cronExpression.trim() && cronHumanReadable && (
                              <InputRightElement
                                width="1.75rem"
                                height="100%"
                                pointerEvents="auto"
                              >
                                <Box
                                  as="button"
                                  type="button"
                                  display="inline-flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  w="1.25rem"
                                  h="1.25rem"
                                  position="relative"
                                  borderRadius="sm"
                                  onClick={() => {
                                    if (!cronHumanReadable) return;
                                    setShowCronHumanReadable((prev) => !prev);
                                  }}
                                  _hover={{ bg: "gray.100" }}
                                >
                                  <Icon
                                    as={
                                      showCronHumanReadable && cronHumanReadable ? FaEdit : FaCheck
                                    }
                                    boxSize={3}
                                    color="gray.500"
                                  />
                                </Box>
                              </InputRightElement>
                            )}
                          </InputGroup>
                        </Tooltip>
                      </Box>
                      <Box flex="1">
                        <FormLabel fontSize="xs" fontWeight="600" mb={0.5} color="transparent" userSelect="none">
                          &nbsp;
                        </FormLabel>
                        <HStack spacing={2} align="center" h="2rem">
                          <Switch
                            size="sm"
                            isChecked={isScheduleActive}
                            onChange={(e) => setIsScheduleActive(e.target.checked)}
                            colorScheme="green"
                            mb={0}
                          />
                          <HStack spacing={1} align="center"
                            bg={isScheduleActive ? "green.50" : "gray.50"}
                            borderRadius="md"
                            px={2}
                            py={1}
                          >
                            {isScheduleActive ? (
                              <Box
                                w="7px"
                                h="7px"
                                borderRadius="full"
                                bg="green.400"
                                flexShrink={0}
                              />
                            ) : (
                              <Icon as={FaPause} boxSize={2} color="gray.500" />
                            )}
                            <Text fontSize="xs" fontWeight="700" color={isScheduleActive ? "green.700" : "gray.500"} mb={0}>
                              {isScheduleActive ? "LIVE" : "Paused"}
                            </Text>
                          </HStack>
                        </HStack>
                      </Box>
                    </Flex>
                  </FormControl>

                  {/* Cron + Slack channel */}
                  {/* <SimpleGrid columns={2} spacing={2} alignItems="flex-end">
                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="600" mb={0.5}>Slack channel</FormLabel>
                      <Select
                        size="sm"
                        value={selectedSlackChannel?.id ?? ""}
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) setSelectedSlackChannel(null);
                          else setSelectedSlackChannel(slackChannels.find((c) => c.id === id) ?? null);
                        }}
                        placeholder="Choose channel"
                        fontSize="xs"
                        h="2rem"
                        _focus={{
                          borderColor: "purple.500",
                          boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                        }}
                        borderRadius="md"
                      >
                        {slackChannels.map((ch) => (
                          <option key={ch.id} value={ch.id}>{ch.name}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid> */}

                  {/* Historical + Temporary instructions */}
                  <SimpleGrid columns={1} spacing={2}>
                    {/* <FormControl>
                      <FormLabel fontSize="xs" fontWeight="600" mb={0.5}>
                        Historical Instructions
                      </FormLabel>
                      <Textarea
                        size="sm"
                        value={historicalInstructions}
                        onChange={(e) => setHistoricalInstructions(e.target.value)}
                        placeholder="Instructions for loading historical context from past runs…"
                        fontSize="xs"
                        rows={5}
                        resize="none"
                        _focus={{
                          borderColor: "purple.500",
                          boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                        }}
                      />
                    </FormControl> */}
                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="600" mb={0.5}>
                        Temporary Instructions
                      </FormLabel>
                      <Textarea
                        size="sm"
                        value={temporaryInstructions}
                        onChange={(e) => setTemporaryInstructions(e.target.value)}
                        placeholder="One-off instructions that override defaults for a single run…"
                        fontSize="xs"
                        rows={5}
                        resize="none"
                        _focus={{
                          borderColor: "purple.500",
                          boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                        }}
                      />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </Box>
            )}
          </Box>

          {/* ── Footer Actions ── */}
          <HStack px={2} pt={1} pb={2} bg="white" justify="end">
            {isChanged && (
              <HStack spacing={2}>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<Icon as={FaUndo} boxSize={3} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDiscard();
                  }}
                  isDisabled={isSaving}
                  fontWeight="500"
                >
                  Discard Changes
                </Button>
                <Button
                  size="xs"
                  colorScheme="green"
                  leftIcon={<Icon as={FaCheck} boxSize={3} />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleSave();
                  }}
                  isLoading={isSaving}
                  fontWeight="500"
                >
                  <Text fontSize="xs" fontWeight="700" mb={0}>
                    {isSavedInAnalyst ? "Update" : "Save"}
                  </Text>
                </Button>
              </HStack>
            )}
          </HStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ScenarioArtifactCard;
